import React from 'react'
import { Row, Col } from 'react-bootstrap'
import { FaChartLine } from 'react-icons/fa'
import styled from 'styled-components'
import { Model } from '../../types/models/model'
import StarRating from '../StarRating'
import { rateBBoxModel, rateClassificationModel } from '../../utils'

const StepCard = styled.div`
  background: #2a2a2a;
  border: none;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const StepHeader = styled.div`
  background: #2a2a2a;
  padding: 1rem 1.5rem;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StepContent = styled.div`
  padding: 1.5rem;
`

const StatusCard = styled.div`
  background: rgba(0, 0, 0, 0.2);
  padding: 0.75rem;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  span {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.85rem;
  }

  strong {
    color: #fff;
    font-size: 0.9rem;
  }
`

interface ModelPerformanceCardProps {
  model: Model
  metrics: {
    mAP: number
    f1Score: number
    precision: number
    recall: number
  }
  projectAnnotationType?: string
  // gradient: string
}

const ModelPerformanceCard: React.FC<ModelPerformanceCardProps> = ({
  model,
  metrics,
  projectAnnotationType,
  // gradient,
}) => {
  return (
    <StepCard>
      <StepHeader>
        {model?.name || 'Model'}
        <FaChartLine />
      </StepHeader>
      <StepContent>
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3">
            <StarRating
              rating={
                projectAnnotationType === 'classification'
                  ? rateClassificationModel(model).classificationRating
                  : rateBBoxModel(model).bBoxRating
              }
              small={false}
            />
          </div>
          <Row className="g-2 mt-3">
            <Col xs={6}>
              <StatusCard>
                <span>Status</span>
                <strong>{model?.state}</strong>
              </StatusCard>
            </Col>
            <Col xs={6}>
              <StatusCard>
                <span>Created</span>
                <strong>
                  {model?.created_at ? new Date(model.created_at).toLocaleDateString() : 'N/A'}
                </strong>
              </StatusCard>
            </Col>
            <Col xs={12}>
              <StatusCard>
                <span>Training Time</span>
                <strong>
                  {model?.train_started_at && model?.train_ended_at
                    ? model.train_ended_at === '0001-01-01T00:00:00Z'
                      ? 'In Progress'
                      : (() => {
                          const trainStart = new Date(model.train_started_at)
                          const trainEnd = new Date(model.train_ended_at)
                          const durationMs = trainEnd.getTime() - trainStart.getTime()
                          const hours = Math.floor(durationMs / (1000 * 60 * 60))
                          const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))
                          return `${hours}h ${minutes}m`
                        })()
                    : 'N/A'}
                </strong>
              </StatusCard>
            </Col>
          </Row>
        </div>

        <div>
          <h6 className="text-white mb-3">Performance Metrics</h6>
          {projectAnnotationType === 'bounding_box' ? (
            <Row className="g-2">
              <Col xs={6}>
                <StatusCard>
                  <span>mAP50-95</span>
                  <strong>{(metrics?.mAP ?? 0).toFixed(2)}%</strong>
                </StatusCard>
              </Col>
              <Col xs={6}>
                <StatusCard>
                  <span>F1 Score</span>
                  <strong>{(metrics?.f1Score ?? 0).toFixed(2)}%</strong>
                </StatusCard>
              </Col>
              <Col xs={6}>
                <StatusCard>
                  <span>Precision</span>
                  <strong>{(metrics?.precision ?? 0).toFixed(2)}%</strong>
                </StatusCard>
              </Col>
              <Col xs={6}>
                <StatusCard>
                  <span>Recall</span>
                  <strong>{(metrics?.recall ?? 0).toFixed(2)}%</strong>
                </StatusCard>
              </Col>
            </Row>
          ) : (
            <StatusCard>
              <span>Accuracy</span>
              <strong>
                {(parseFloat(`${model?.metrics?.['train:accuracy'] ?? '0'}`) * 100).toFixed(0)}%
              </strong>
            </StatusCard>
          )}
        </div>
      </StepContent>
    </StepCard>
  )
}

export default ModelPerformanceCard
